import {showNotification} from 'platform/components';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  UpdateVehicleOnPlatformApiArg,
  useLazyGetSalesPlatformsQuery,
  useUpdateVehicleOnAllPlatformsMutation,
  useUpdateVehicleOnPlatformMutation,
} from '@dms/api/sales';
import {useLazyGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {ApiError} from 'shared/web';

import {handleApiError} from '../utils/handleApiError';
import {usePkwEnvkvAdvertisingPreflight} from './usePkwEnvkvAdvertisingPreflight';

export function useAdPlatformsVehicleUpdate() {
  const [getSalesPlatforms] = useLazyGetSalesPlatformsQuery();
  const [getSaleVehicle] = useLazyGetSaleVehicleQuery();
  const [updateVehicleOnAllPlatforms] = useUpdateVehicleOnAllPlatformsMutation();
  const [updateVehicleOnSinglePlatform] = useUpdateVehicleOnPlatformMutation();
  const {runPkwEnvkvPreflight} = usePkwEnvkvAdvertisingPreflight();

  async function hasPublishedAdvertisements(vehicleId: string) {
    const saleVehicle = await getSaleVehicle({vehicleId});

    if (isNotNil(saleVehicle.error)) {
      return false;
    }

    return getSalesPlatforms({vehicleId})
      .unwrap()
      .then((advertisements) =>
        advertisements.some((ad) => ad.enabled && ad.status === 'published')
      )
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });
  }

  async function updateAds(
    vehicleId: string,
    platformCode?: UpdateVehicleOnPlatformApiArg['platformCode']
  ) {
    // The same fresh EnVKV preflight guards the update-all and the
    // update-single path; a RED vehicle never reaches the update mutation.
    const isPassed = await runPkwEnvkvPreflight(vehicleId);
    if (!isPassed) {
      return false;
    }

    const mutation =
      isNil(platformCode) || isEmpty(platformCode)
        ? updateVehicleOnAllPlatforms({vehicleId})
        : updateVehicleOnSinglePlatform({vehicleId, platformCode});

    return mutation
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.vehicle.labels.classifiedsWillBeUpdated'));
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });
  }

  return {hasPublishedAdvertisements, updateAds};
}
